import { Component, computed, effect, OnInit, signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ModalConfig } from '@app/shared/models/modal.model';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { SharedModule } from '@app/shared/shared.module';
import { TrophyStatus } from '@app/home/trophies/trophies.model';
import { TesterModalService } from '../tester-modal.service';
import { TesterModalFacade } from '../tester-modal.facade';
import { Game } from '@app/shared/models/game.model';
import { LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { RealtimeService } from '@app/shared/services/realtime.service';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { LoggerService } from '@app/shared/services/logger.service';
import { Observable, takeUntil } from 'rxjs';
import { GamesRepository } from '@app/shared/state/games/games.repository';
import { EventsRepository } from '@app/shared/state/events/events.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-tester-advanced-options',
  standalone: true,
  imports: [FormsModule, SharedModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './advanced-options.component.html',
  styleUrl: '../tester-modal.component.scss',
})
export class AdvancedOptionsComponent implements OnInit {
  testerModalData = inject<ModalConfig>(MAT_DIALOG_DATA);
  private formBuilder = inject(FormBuilder);
  private testerModalService = inject(TesterModalService);
  private testerModalFacade = inject(TesterModalFacade);
  private realtimeService = inject(RealtimeService);
  private logger = inject(LoggerService);
  private gamesRepo = inject(GamesRepository);
  private eventsRepo = inject(EventsRepository);

  private componentDestroy: () => Observable<unknown>;

  sortedGames!: Game[];

  fakeSpinTypeForm!: FormGroup;

  completeLteStatusForm!: FormGroup;
  selectedEventToComplete!: string;

  trophyForm!: FormGroup;
  selectedGame!: string;
  selectedTrophyType!: string;

  gameIdToFakeSpin!: string;
  betPrizeFakeSpin!: number;
  winPrizeFakeSpin!: number;
  addUndefinedGameBonusGameFeature!: boolean;
  addFreeSpinsBonusGameFeature!: boolean;
  addBuyBonusGame!: boolean;

  fakePurchaseForm!: FormGroup;

  readonly selectedRateId = signal<string>('');

  readonly purchaseRates = computed(() => this.testerModalFacade.allRatesFaked());

  readonly selectedRate = computed(() => this.purchaseRates().find((rate) => rate.id === this.selectedRateId()));

  challengeLimitedTimeEvents = this.testerModalFacade.getChallengeEvents();

  constructor() {
    this.fakePurchaseForm = this.formBuilder.group({
      selectedSku: [''],
      selectedType: [{ value: '', disabled: true }],
      selectedProductType: [{ value: '', disabled: true }],
      selectedAmount: [{ value: null, disabled: true }],
      selectedPriceEur: [{ value: null, disabled: true }],
    });

    this.fakePurchaseForm.get('selectedSku')!.valueChanges.subscribe((value) => {
      this.selectedRateId.set(value);
    });

    effect(() => {
      const rate = this.selectedRate();
      if (!rate) return;

      this.fakePurchaseForm.patchValue(
        {
          selectedType: rate.type,
          selectedProductType: rate.productType,
          selectedAmount: rate.amount,
          selectedPriceEur: rate.priceEur,
        },
        { emitEvent: false }
      );
    });
  }

  ngOnInit(): void {
    this.realtimeService
      .on('Trophy.Status')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: (trophyStatus: TrophyStatus) => {
          const game = this.gamesRepo.getGameById(trophyStatus.gameId);
          game.trophy.current = game.trophy.current + trophyStatus.increment;
          this.gamesRepo.updateGameTrophyById(trophyStatus.gameId, game.trophy);
        },
        error: (error) => this.logger.error(error),
      });

    this.realtimeService
      .on('LimitedTimeEventStatus')
      .pipe(takeUntil(this.componentDestroy()))
      .subscribe({
        next: (newEventStatus: Partial<LimitedTimeEvent>) => {
          this.eventsRepo.updateLimitedTimeEventById(newEventStatus.eventId, newEventStatus);
        },
        error: (error) => this.logger.error(error),
      });

    this.sortedGames = this.testerModalFacade.getSortedGames();

    this.trophyForm = this.formBuilder.group({
      selectedGame: [this.selectedGame],
      selectedTrophyType: [this.selectedTrophyType],
    });

    this.trophyForm.get('selectedGame')?.valueChanges.subscribe((value) => {
      this.selectedGame = value;
    });

    this.completeLteStatusForm = this.formBuilder.group({
      selectedEventToComplete: [this.selectedEventToComplete],
    });

    this.fakeSpinTypeForm = this.formBuilder.group({
      bet: [this.betPrizeFakeSpin],
      win: [this.winPrizeFakeSpin],
      gameId: [this.gameIdToFakeSpin],
      undefinedGameBonusGameFeature: [this.addUndefinedGameBonusGameFeature],
      freeSpinsBonusGameFeature: [this.addFreeSpinsBonusGameFeature],
      buyBonusGame: [this.addBuyBonusGame],
    });

    this.fakeSpinTypeForm.get('bet')?.valueChanges.subscribe((value) => {
      this.betPrizeFakeSpin = value;
    });

    this.fakeSpinTypeForm.get('win')?.valueChanges.subscribe((value) => {
      this.winPrizeFakeSpin = value;
    });

    this.fakeSpinTypeForm.get('gameId')?.valueChanges.subscribe((value) => {
      this.gameIdToFakeSpin = value;
    });

    this.fakeSpinTypeForm.get('undefinedGameBonusGameFeature')?.valueChanges.subscribe((value) => {
      this.addUndefinedGameBonusGameFeature = value;
    });

    this.fakeSpinTypeForm.get('freeSpinsBonusGameFeature')?.valueChanges.subscribe((value) => {
      this.addFreeSpinsBonusGameFeature = value;
    });

    this.fakeSpinTypeForm.get('buyBonusGame')?.valueChanges.subscribe((value) => {
      this.addBuyBonusGame = value;
    });
  }

  async completeEventIdStatus(): Promise<void> {
    const eventId: string = this.completeLteStatusForm.get('selectedEventToComplete')?.value;
    this.testerModalService.setChallengesOnePointToComplete(eventId);
  }

  async completeGameTrophy(): Promise<void> {
    const gameId: string = this.trophyForm.get('selectedGame')?.value;
    this.testerModalService.setTrophyOnePointToComplete(gameId);
  }

  async doFakePurchase(): Promise<void> {
    this.testerModalService.fakePurchase({
      sku: this.selectedRate()?.sku,
      productType: this.fakePurchaseForm.get('selectedProductType')?.value,
      amount: this.fakePurchaseForm.get('selectedAmount')?.value,
      priceEur: parseInt(this.fakePurchaseForm.get('selectedPriceEur')?.value),
    });
  }

  checkInvalidFakeSpinValues(): boolean {
    return this.betPrizeFakeSpin <= 0 || this.winPrizeFakeSpin <= 0 || this.gameIdToFakeSpin == null;
  }

  async doFakeSpin(): Promise<void> {
    if (this.checkInvalidFakeSpinValues()) return;

    this.testerModalService.fakePlay({
      bet: this.betPrizeFakeSpin,
      win: this.winPrizeFakeSpin,
      gameId: this.gameIdToFakeSpin,
      addUndefinedGameBonusGameFeature: this.addUndefinedGameBonusGameFeature,
      addFreeSpinsBonusGameFeature: this.addFreeSpinsBonusGameFeature,
      addBuyBonusGame: this.addBuyBonusGame,
    });
  }

  async fixUserTrophies(): Promise<void> {
    await this.testerModalService.fixUserTrophies();
  }
}
